import { useId, useState } from 'react';

import { cn } from '@shared/lib/cn';
import { Card, type CardShadow } from '@shared/ui/card';
import { Dialog } from '@shared/ui/dialog';
import { Divider } from '@shared/ui/divider';
import { Icon } from '@shared/ui/icon';
import { IconButton } from '@shared/ui/icon-button';
import { Switch } from '@shared/ui/switch';
import { Tab } from '@shared/ui/tabs';
import { Textarea } from '@shared/ui/textarea';

import { CHART_TEXT } from './chart.text';
import { ChartLegendDot } from './chart-legend-dot.ui';
import type { GraphCardLegendItem, GraphCardPeriod } from './model/graph-card.types';
import type {
  TimeSeries,
  TimeSeriesBreak,
  TimeSeriesChartOverlay,
  TimeSeriesChartSeries,
  TimeSeriesChartType,
} from './model/time-series.types';
import { TimeSeriesChart } from './time-series-chart.ui';

export type GraphCardSize = 'card' | 'popup';

export interface GraphCardProps {
  /** 카드 제목(heading-4). 서비스 문구는 부르는 쪽이 넘긴다 */
  title: string;
  /** 그래프 종류 → TimeSeriesChart `type` */
  chart: TimeSeriesChartType;
  /** 계열 구성 → TimeSeriesChart `series` */
  series: TimeSeriesChartSeries;
  /** Figma `size`. card = 카드, popup = 넓힌 확대 팝업. 기본 card */
  size?: GraphCardSize;
  /** 보일 기간 탭 */
  periods: GraphCardPeriod[];
  period?: GraphCardPeriod;
  defaultPeriod?: GraphCardPeriod;
  onPeriodChange?: (period: GraphCardPeriod) => void;
  data: TimeSeries[];
  unit?: string;
  overlay?: TimeSeriesChartOverlay;
  /** → TimeSeriesChart `formatValue` · `formatTick` · `formatTooltipValue` · `breaks` · `tooltipNote` */
  formatValue?: (value: number) => string;
  formatTick?: (value: number) => string;
  formatTooltipValue?: (value: number) => string;
  breaks?: TimeSeriesBreak[];
  tooltipNote?: string;
  /** 머리말 오른쪽 범례. 없으면 그리지 않는다 */
  legend?: GraphCardLegendItem[];
  /** Figma `Show connected` — 연결매출 스위치. 없으면 그리지 않는다 */
  connected?: {
    checked?: boolean;
    defaultChecked?: boolean;
    onCheckedChange?: (checked: boolean) => void;
  };
  /** Figma `Show comment` — 코멘트(size=card 에만). 없으면 그리지 않는다 */
  comment?: {
    value?: string;
    defaultValue?: string;
    readOnly?: boolean;
    onChange?: (value: string) => void;
    onBlur?: () => void;
  };
  /** size=card 의 확대 단추 */
  onExpand?: () => void;
  /** size=popup 의 닫기 단추(Esc · 배경 누름도 같다) */
  onClose?: () => void;
  /**
   * size=popup 을 portal · 덮개 · 포커스 가두기 없이 제자리에 그린다(fe-rules 3절 — 확인 페이지).
   * 기본 false = 화면 가운데 팝업(Dialog size=lg, 폭 `size.dialog.lg` 1200)
   */
  inline?: boolean;
  /**
   * 카드 그림자. 기본 accent(청록 — 포트폴리오 그래프 5종), 펀드의 회수 및 평가 총액은 default.
   * 청사진 props 에 없어 더했다(Figma valuation 은 elevation/card).
   */
  shadow?: CardShadow;
  className?: string;
}

/**
 * 그래프 카드 틀: 제목 + 기간 탭 + (연결매출) + (범례) + 확대 단추 + 그래프 + (코멘트).
 * size=popup 은 확대 팝업 — 공통 Dialog(size=lg · 바닥 없음, role=dialog 한 겹)로 그린다:
 * 머리(제목 · 범례 · 닫기, 고정) | 머리 아래 줄(기간 탭 · 연결매출, 고정) | 본문(그래프, 스크롤). 코멘트는 없다.
 * 부르는 쪽은 `overlay.open(({ close }) => <GraphCard size="popup" onClose={close} … />)` 로 띄운다.
 * 그래프 자리는 TimeSeriesChart(design-preview 에서는 빈 상자).
 */
export function GraphCard({
  title,
  chart,
  series,
  size = 'card',
  periods,
  period,
  defaultPeriod,
  onPeriodChange,
  data,
  unit,
  overlay,
  formatValue,
  formatTick,
  formatTooltipValue,
  breaks,
  tooltipNote,
  legend,
  connected,
  comment,
  onExpand,
  onClose,
  inline = false,
  shadow = 'accent',
  className,
}: GraphCardProps) {
  const titleId = useId();
  const [innerPeriod, setInnerPeriod] = useState<GraphCardPeriod | undefined>(
    defaultPeriod ?? periods[0],
  );
  const currentPeriod = period ?? innerPeriod;
  const isPopup = size === 'popup';

  const selectPeriod = (next: GraphCardPeriod) => {
    if (period === undefined) setInnerPeriod(next);
    onPeriodChange?.(next);
  };

  const periodControls = (
    <div className="flex items-center gap-2-5">
      <div role="tablist" aria-label={CHART_TEXT.periodTabs} className="flex gap-2-5">
        {periods.map((item) => (
          <Tab
            key={item}
            size="sm"
            active={item === currentPeriod}
            onClick={() => selectPeriod(item)}
          >
            {CHART_TEXT.period[item]}
          </Tab>
        ))}
      </div>
      {connected ? (
        <div className="flex h-5 items-center gap-3">
          <Divider orientation="vertical" decorative />
          <span className="text-fg-default typo-body-sm-strong">{CHART_TEXT.connected}</span>
          <Switch
            aria-label={CHART_TEXT.connected}
            checked={connected.checked}
            defaultChecked={connected.defaultChecked}
            onCheckedChange={connected.onCheckedChange}
          />
        </div>
      ) : null}
    </div>
  );

  const legendList =
    legend && legend.length > 0 ? (
      <ul className="flex items-center gap-3">
        {legend.map((item) => (
          <li key={`${item.series}-${item.label}`} className="flex">
            <ChartLegendDot kind="series" step={item.series}>
              {item.label}
            </ChartLegendDot>
          </li>
        ))}
      </ul>
    ) : undefined;

  const chartBox = (
    <TimeSeriesChart
      type={chart}
      series={series}
      overlay={overlay}
      data={data}
      unit={unit}
      extend={isPopup}
      formatValue={formatValue}
      formatTick={formatTick}
      formatTooltipValue={formatTooltipValue}
      breaks={breaks}
      tooltipNote={tooltipNote}
    />
  );

  if (isPopup) {
    // 확대 팝업 = 공통 Dialog lg(1200) · 바닥 없음 (fe-rules 3절 — 머리 · 머리 아래 줄 고정, 본문만 스크롤).
    // 머리 = 제목 + 범례(headerActions) + 닫기, 머리 아래 줄 = 기간 탭 · 연결매출, 본문 = 그래프
    return (
      <Dialog
        open
        inline={inline}
        size="lg"
        showFooter={false}
        title={title}
        headerActions={legendList}
        subheader={periodControls}
        onOpenChange={(next) => {
          if (!next) onClose?.();
        }}
        className={cn(inline && 'max-w-full', className)}
      >
        {chartBox}
      </Dialog>
    );
  }

  return (
    <Card
      as="section"
      shadow={shadow}
      data-size={size}
      aria-labelledby={titleId}
      className={cn('w-full gap-5', className)}
    >
      <div className="flex h-(--size-control-sm) items-center justify-between">
        <div className="flex items-center gap-5">
          <h3 id={titleId} className="text-fg-default typo-heading-4">
            {title}
          </h3>
          {periodControls}
        </div>
        <div className="flex items-center gap-5">
          {legendList}
          {/* 확대: primary 원(24) + 흰 돋보기(16). 기존 btn_graphsearch.svg(24 원 #00ADA0 + 흰 돋보기)와 같다 */}
          <IconButton
            variant="primary"
            shape="circle"
            size="xs"
            aria-label={CHART_TEXT.expand}
            icon={<Icon name="search" size="sm" />}
            onClick={onExpand}
          />
        </div>
      </div>
      {chartBox}
      {comment ? (
        <Textarea
          tone="soft"
          aria-label={CHART_TEXT.commentLabel(title)}
          value={comment.value}
          defaultValue={comment.defaultValue}
          readOnly={comment.readOnly}
          onChange={(event) => comment.onChange?.(event.target.value)}
          onBlur={() => comment.onBlur?.()}
        />
      ) : null}
    </Card>
  );
}
